import type { Matter } from "@/shared/cache";

export type SearchableMatter = Pick<Matter, "matter_id" | "matter" | "description" | "client">;

// Numeric-aware so "128430.1018" ranks above "128430.851" rather than below it,
// and so the leading zero in "02300.100" doesn't skew the comparison.
const COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

// Matters one client contributes before the next client gets a turn. Keeps a
// 300-matter client off the whole first page of a broad query.
const BAND_SIZE = 3;

export function getRecentMatters(matters: Matter[]): Matter[] {
  return matters
    .filter((m) => m.recency_rank != null)
    .sort((a, b) => (a.recency_rank ?? 0) - (b.recency_rank ?? 0));
}

// How many query tokens the client name itself contains. A client matching
// every token outranks one that only matched through a matter description.
function clientTier(client: string, tokens: string[]): number {
  const lower = client.toLowerCase();
  let tier = 0;
  for (const token of tokens) {
    if (lower.includes(token)) tier++;
  }
  return tier;
}

function byMatterDesc(a: SearchableMatter, b: SearchableMatter): number {
  return COLLATOR.compare(b.matter, a.matter);
}

export function searchMatters<T extends SearchableMatter>(
  matters: readonly T[],
  query: string,
  caseListIds?: Set<string>,
): T[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const tokens = q.split(/\s+/);

  const pinned: T[] = [];
  const byClient = new Map<string, T[]>();

  for (const m of matters) {
    const desc = m.description.toLowerCase();
    const client = m.client.toLowerCase();
    const matter = m.matter.toLowerCase();
    const matched = tokens.every(
      (t) => desc.includes(t) || client.includes(t) || matter.includes(t),
    );
    if (!matched) continue;

    if (caseListIds?.has(m.matter_id)) {
      pinned.push(m);
      continue;
    }
    const group = byClient.get(m.client);
    if (group) group.push(m);
    else byClient.set(m.client, [m]);
  }

  pinned.sort(byMatterDesc);

  const groups = Array.from(byClient, ([client, items]) => {
    items.sort(byMatterDesc);
    return { tier: clientTier(client, tokens), items };
  }).sort((a, b) => b.tier - a.tier || byMatterDesc(a.items[0], b.items[0]));

  const results = pinned;
  for (let start = 0; start < groups.length; ) {
    let end = start;
    while (end < groups.length && groups[end].tier === groups[start].tier) end++;
    // Interleave only within a tier, so a client that matched every token is
    // never interrupted by one that matched less of the query.
    const tier = groups.slice(start, end);
    for (let offset = 0, more = true; more; offset += BAND_SIZE) {
      more = false;
      for (const group of tier) {
        if (offset >= group.items.length) continue;
        results.push(...group.items.slice(offset, offset + BAND_SIZE));
        more = true;
      }
    }
    start = end;
  }

  return results;
}
